import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatPostDateShort as formatDate } from "@/lib/date";
import AdminShell from "@/components/AdminShell";
import { toast } from "sonner";
import { AlertTriangle, Trash2, RefreshCw } from "lucide-react";

const AdminDuplicates = () => {
  const [by, setBy] = useState("title"); // title | content
  const [groups, setGroups] = useState([]);
  const [stats, setStats] = useState({ total_groups: 0, total_duplicates: 0 });
  const [loading, setLoading] = useState(true);
  // map postId -> "delete" | "keep"; default first (oldest) kept, others delete
  const [marks, setMarks] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/duplicates", { params: { by } });
      setGroups(data.groups || []);
      setStats({ total_groups: data.total_groups || 0, total_duplicates: data.total_duplicates || 0 });
      // auto-mark: keep first, delete others
      const m = {};
      for (const g of data.groups || []) {
        g.posts.forEach((p, idx) => { m[p.id] = idx === 0 ? "keep" : "delete"; });
      }
      setMarks(m);
    } catch (e) {
      toast.error("Errore caricamento duplicati");
    } finally {
      setLoading(false);
    }
  }, [by]);

  useEffect(() => { load(); }, [load]);

  const selectedIds = useMemo(
    () => Object.entries(marks).filter(([, v]) => v === "delete").map(([k]) => k),
    [marks]
  );

  const toggle = (id) => setMarks((m) => ({ ...m, [id]: m[id] === "delete" ? "keep" : "delete" }));

  const setGroupAllDelete = (group, exceptIdx) => {
    setMarks((m) => {
      const next = { ...m };
      group.posts.forEach((p, i) => { next[p.id] = i === exceptIdx ? "keep" : "delete"; });
      return next;
    });
  };

  const runBulkDelete = async (ids) => {
    if (!ids.length) return;
    if (!window.confirm(`Eliminare definitivamente ${ids.length} post duplicati?`)) return;
    setBusy(true);
    try {
      // send in chunks of 500 to be safe
      let deleted = 0;
      for (let i = 0; i < ids.length; i += 500) {
        const chunk = ids.slice(i, i + 500);
        const { data } = await api.post("/admin/posts/bulk-delete", { ids: chunk });
        deleted += data.deleted || 0;
      }
      toast.success(`${deleted} post eliminati`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore eliminazione");
    } finally {
      setBusy(false);
    }
  };

  const autoClean = () => {
    // keep first of each group, delete the rest
    const ids = [];
    for (const g of groups) {
      g.posts.forEach((p, idx) => { if (idx > 0) ids.push(p.id); });
    }
    runBulkDelete(ids);
  };

  return (
    <AdminShell
      title="Duplicati"
      actions={
        <button
          data-testid="btn-reload-dup"
          onClick={load}
          disabled={loading}
          className="flex items-center gap-2 px-3 sm:px-4 py-2.5 font-sans text-[10px] sm:text-xs tracking-[0.25em] uppercase border border-[#1a1918] hover:bg-[#1a1918] hover:text-[#f9f8f6] transition-colors disabled:opacity-50"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> ricarica
        </button>
      }
    >
      <div className="max-w-4xl">
        <p className="font-serif italic text-base sm:text-lg text-[#4a4843] mb-6 sm:mb-8 leading-relaxed">
          Trova i post che hanno lo stesso titolo o lo stesso contenuto. Per ogni gruppo il più vecchio è tenuto e gli altri sono marcati per la cancellazione — puoi cambiare con un tocco.
        </p>

        <div className="flex flex-wrap gap-2 mb-6 sm:mb-8">
          <button
            data-testid="dup-by-title"
            onClick={() => setBy("title")}
            className={`px-4 py-2 font-sans text-[10px] sm:text-xs tracking-[0.25em] uppercase border transition-colors ${
              by === "title" ? "bg-[#1a1918] text-[#f9f8f6] border-[#1a1918]" : "border-[#1a1918] text-[#1a1918] hover:bg-[#e5e1d8]"
            }`}
          >per titolo</button>
          <button
            data-testid="dup-by-content"
            onClick={() => setBy("content")}
            className={`px-4 py-2 font-sans text-[10px] sm:text-xs tracking-[0.25em] uppercase border transition-colors ${
              by === "content" ? "bg-[#1a1918] text-[#f9f8f6] border-[#1a1918]" : "border-[#1a1918] text-[#1a1918] hover:bg-[#e5e1d8]"
            }`}
          >per contenuto</button>
        </div>

        {!loading && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-6 mb-8 max-w-xl">
            <div data-testid="stat-dup-groups" className="border border-[#e5e1d8] p-4 sm:p-6">
              <div className="font-serif text-3xl sm:text-4xl text-[#1a1918] font-light">{stats.total_groups}</div>
              <div className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#4a4843] mt-1">gruppi</div>
            </div>
            <div data-testid="stat-dup-count" className="border border-[#e5e1d8] p-4 sm:p-6">
              <div className="font-serif text-3xl sm:text-4xl text-[#9e4747] font-light">{stats.total_duplicates}</div>
              <div className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#4a4843] mt-1">da eliminare</div>
            </div>
            <div data-testid="stat-dup-selected" className="border border-[#e5e1d8] p-4 sm:p-6 col-span-2 sm:col-span-1">
              <div className="font-serif text-3xl sm:text-4xl text-[#1a1918] font-light">{selectedIds.length}</div>
              <div className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#4a4843] mt-1">selezionati</div>
            </div>
          </div>
        )}

        {loading ? (
          <p className="font-sans text-sm text-[#4a4843]">caricamento…</p>
        ) : groups.length === 0 ? (
          <div data-testid="dup-empty" className="border border-[#e5e1d8] p-10 text-center">
            <p className="font-serif text-2xl text-[#1a1918] mb-2">Nessun duplicato trovato</p>
            <p className="font-sans text-sm text-[#4a4843]">
              Ogni post ha {by === "title" ? "un titolo" : "un contenuto"} unico.
            </p>
          </div>
        ) : (
          <>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6 p-4 bg-[#f2efe9] border-l-2 border-[#d4b886]">
              <AlertTriangle size={18} className="text-[#9e4747] shrink-0" />
              <p className="font-sans text-sm text-[#4a4843] flex-1">
                Pulizia rapida: tieni il più vecchio per ogni gruppo, cancella gli altri.
              </p>
              <button
                data-testid="btn-auto-clean"
                onClick={autoClean}
                disabled={busy || stats.total_duplicates === 0}
                className="bg-[#9e4747] text-[#f9f8f6] hover:bg-[#1a1918] transition-colors px-4 py-2.5 font-sans text-[10px] sm:text-xs tracking-[0.25em] uppercase disabled:opacity-50"
              >
                pulizia automatica ({stats.total_duplicates})
              </button>
            </div>

            <div className="mb-6 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between p-3 border border-[#1a1918] bg-[#f9f8f6] sticky top-[57px] lg:top-0 z-10">
              <span className="font-sans text-xs tracking-[0.2em] uppercase text-[#1a1918]">
                {selectedIds.length} post marcati per l'eliminazione
              </span>
              <button
                data-testid="btn-bulk-delete"
                onClick={() => runBulkDelete(selectedIds)}
                disabled={busy || selectedIds.length === 0}
                className="flex items-center justify-center gap-2 bg-[#1a1918] text-[#f9f8f6] hover:bg-[#9e4747] transition-colors px-5 py-2.5 font-sans text-[10px] sm:text-xs tracking-[0.25em] uppercase disabled:opacity-50"
              >
                <Trash2 size={14} /> elimina selezionati
              </button>
            </div>

            <ul className="space-y-6">
              {groups.map((g, gi) => (
                <li key={g.key + gi} data-testid={`dup-group-${gi}`} className="border border-[#e5e1d8]">
                  <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 bg-[#f2efe9] border-b border-[#e5e1d8]">
                    <p className="font-serif text-lg sm:text-xl text-[#1a1918] flex-1 min-w-0 truncate">
                      {by === "title" ? g.posts[0].title : `“${g.posts[0].title}” · ${g.count} copie`}
                    </p>
                    <span className="font-sans text-[10px] tracking-[0.25em] uppercase bg-[#1a1918] text-[#f9f8f6] px-2 py-1">
                      {g.count} copie
                    </span>
                  </div>
                  <ul className="divide-y divide-[#e5e1d8]">
                    {g.posts.map((p, idx) => {
                      const marked = marks[p.id] === "delete";
                      return (
                        <li key={p.id} data-testid={`dup-row-${p.id}`} className={`flex items-start gap-3 px-4 py-3 transition-colors ${marked ? "bg-[#9e4747]/5" : ""}`}>
                          <label className="flex items-center pt-1 shrink-0 cursor-pointer">
                            <input
                              type="checkbox"
                              data-testid={`dup-check-${p.id}`}
                              checked={marked}
                              onChange={() => toggle(p.id)}
                              className="w-5 h-5 accent-[#9e4747] cursor-pointer"
                            />
                          </label>
                          <div className="flex-1 min-w-0">
                            <Link
                              to={`/admin/edit/${p.id}`}
                              className="font-serif text-base sm:text-lg text-[#1a1918] hover:text-[#4a4843] block truncate"
                            >
                              {p.title || "(senza titolo)"}
                            </Link>
                            {p.author && (
                              <div className="font-serif italic text-xs sm:text-sm text-[#4a4843] mt-0.5">
                                di {p.author}
                              </div>
                            )}
                            <div className="flex flex-wrap gap-x-3 gap-y-1 font-sans text-[10px] tracking-[0.2em] uppercase text-[#4a4843] mt-1">
                              <span>{formatDate(p.created_at)}</span>
                              {p.source_filename && <span>· {p.source_filename}</span>}
                              {p.published ? (
                                <span className="text-[#477d55]">· pubblicato</span>
                              ) : (
                                <span>· bozza</span>
                              )}
                              {idx === 0 && <span className="text-[#d4b886] font-semibold">· il più vecchio</span>}
                            </div>
                            {p.excerpt && (
                              <p className="font-serif italic text-sm text-[#4a4843] mt-2 line-clamp-2">{p.excerpt}</p>
                            )}
                          </div>
                          <button
                            data-testid={`dup-keep-${p.id}`}
                            onClick={() => setGroupAllDelete(g, idx)}
                            title="Tieni solo questo"
                            className="shrink-0 font-sans text-[10px] tracking-[0.2em] uppercase border border-[#1a1918] text-[#1a1918] px-2 py-1 hover:bg-[#1a1918] hover:text-[#f9f8f6] transition-colors"
                          >
                            tieni questo
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </AdminShell>
  );
};

export default AdminDuplicates;
